import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from '@env/environment';
import { CommonService } from '@app/shared/services/common/common.service';

/**
 * Flattens transparent product-style PNGs onto a white background BEFORE handing them to
 * DesignHuddle for compositing (client SDK `getVariableTemplatePreviewURL` and the export-job
 * REST `product_image` body).
 *
 * WHY: DesignHuddle's product_image compositing API has no background/matte/color parameter —
 * confirmed against DH's own official "Product Image Backgrounds" API doc
 * (https://api.designhuddle.com/doc/Product_Image_Backgrounds.pdf), which lists only
 * product_image_url/width/height/canvas_zone_dimensions/offset/horizontal_align/vertical_align.
 * When the source style PNG has transparent regions (photographic product cutouts, as opposed to
 * flat vector garment art), DH renders its own transparency-indicator checkerboard behind them and
 * flattens it into the returned raster — zero alpha, opaque checkerboard baked into whatever
 * consumes that URL (published Shopify thumbnails, catalog previews, etc). Since DH has no
 * parameter to request a solid matte instead, we matte it ourselves before DH ever sees the image.
 *
 * Used by every component that calls DSHDLib.getVariableTemplatePreviewURL or POSTs an
 * export-job with a product_image/product_image_url body: product-color-selection,
 * master-product-listing, edit-master-product, product-edit-modal. NOT used for the live
 * DSHDLib.Editors.insert() editor view — a checkerboard there is the correct UX cue that an area
 * is transparent while designing; it only matters once a raster is generated/published.
 *
 * Never mutates the stored source asset — only a derived, uploaded copy is used for compositing.
 * Fails soft everywhere (CORS-tainted canvas, image load error, upload error) by resolving with
 * the original raw URL, so a flatten failure never blocks preview generation.
 *
 * Also caps the output at MAX_DIMENSION_PX and re-encodes as JPEG rather than PNG: every consumer
 * passes this URL into DH alongside an explicit product_image_width (500) that DH scales the image
 * down to regardless, so a source photo at native catalog resolution (e.g. 1672x2000, several MB)
 * buys nothing downstream — it just makes the payload heavier than DH's stateless preview endpoint
 * (`getVariableTemplatePreviewURL`, the same `/previews/vtp` call, low-latency by design) can
 * reliably handle; confirmed one product's preview failing with a hard 500 from DH traced directly
 * to its unusually large (2.7MB) flattened PNG. PNG was also the wrong format post-flatten: once
 * we've painted a fully opaque white background behind the source, there is no transparency left to
 * preserve, so PNG's lossless encoding was pure overhead versus a quality JPEG.
 */
@Injectable({
  providedIn: 'root'
})
export class ProductImageMatteService {
  private static readonly MAX_DIMENSION_PX = 1000;
  private flattenedImageUrlCache: Map<string, Observable<string>> = new Map();

  constructor(private commonService: CommonService) { }

  getFlattenedProductImageUrl(rawUrl: string): Observable<string> {
    if (!rawUrl || !/^https?:\/\//i.test(rawUrl)) {
      return of(rawUrl);
    }

    const cached = this.flattenedImageUrlCache.get(rawUrl);
    if (cached) return cached;

    const flattened$ = new Observable<string>(subscriber => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const srcW = img.naturalWidth || 500;
          const srcH = img.naturalHeight || 500;
          const scale = Math.min(1, ProductImageMatteService.MAX_DIMENSION_PX / Math.max(srcW, srcH));

          const canvas = document.createElement('canvas');
          canvas.width = Math.round(srcW * scale);
          canvas.height = Math.round(srcH * scale);
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('2d canvas context unavailable');
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.9); // throws if canvas is CORS-tainted
          const base64 = dataUrl.split(',')[1];

          this.commonService.uploadHuddleLogo({ File: base64, Extension: 'jpg' }).subscribe({
            next: (response: any) => {
              if (response?.StatusCode === 200 && response?.Data?.ResponseData) {
                const flattenedUrl = environment.huddleLogoFolder + response.Data.ResponseData;
                subscriber.next(flattenedUrl);
              } else {
                console.warn(`⚠️ Flatten-to-white upload returned no filename, using raw source:`, rawUrl, response);
                subscriber.next(rawUrl);
              }
              subscriber.complete();
            },
            error: (err) => {
              console.warn(`⚠️ Flatten-to-white upload failed, using raw source:`, rawUrl, err);
              subscriber.next(rawUrl);
              subscriber.complete();
            }
          });
        } catch (e) {
          console.warn(`⚠️ Flatten-to-white canvas draw failed (likely CORS-tainted image), using raw source:`, rawUrl, e);
          subscriber.next(rawUrl);
          subscriber.complete();
        }
      };
      img.onerror = () => {
        console.warn(`⚠️ Flatten-to-white image load failed, using raw source:`, rawUrl);
        subscriber.next(rawUrl);
        subscriber.complete();
      };
      img.src = rawUrl;
    }).pipe(shareReplay(1));

    this.flattenedImageUrlCache.set(rawUrl, flattened$);
    return flattened$;
  }
}
